import { z } from "zod/v4";

import { StyleGuideSchema } from "../../../functions/analytics/store-style-guide/store-style-guide";
import { AuditFunnelSchema } from "../../../functions/opportunity-audit/shopify/shopify";
import { AuditStoreProfileSchema } from "../../../functions/opportunity-audit/context/response";

/**
 * The store briefing the audit shows before the merchant has said anything.
 *
 * One call carries every region, because the panel presents them as one thing.
 * The tool runs its reads concurrently and hands the results over; the model
 * composes prose *about* the briefing but never restates a value from it — a
 * number narrated in chat can drift from the same number in the panel, and then
 * the two disagree about the store in front of them.
 */

/**
 * Why a region is not in the payload.
 *
 * `unavailable` and `failed` are deliberately distinct. A headless store has no
 * theme to read styling from and never will; a timeout might work next time.
 * The work log renders from this, so collapsing them would show the merchant a
 * tick where we learned nothing, or a cross where nothing is wrong.
 */
export const BriefingRegionStatusSchema = z.enum([
  "ok",
  "unavailable",
  "failed",
]);

export const BriefingStoreFactsSchema = z.object({
  storeName: z.string().nullable(),
  currencyCode: z.string().nullable(),
  /** Null on a store with no orders — no history, which is not a zero. */
  orderCount: z.number().nullable(),
  productCount: z.number().nullable(),
  revenue: z.number().nullable(),
  averageOrderValue: z.number().nullable(),
  /** How far back the money figures look, so the panel can label them. */
  windowDays: z.number(),
  /**
   * True when the order read hit its page cap, so revenue and AOV describe the
   * orders we saw rather than every order in the window.
   */
  truncated: z.boolean(),
  /** Who runs the store; Shopify's account name, without a role, when nothing better is known. */
  contact: AuditStoreProfileSchema.shape.contact,
  team: AuditStoreProfileSchema.shape.team,
  stack: AuditStoreProfileSchema.shape.stack,
});

export const BriefingBrandSchema = z.object({
  /** The URL actually rendered, so a surprising result can be reproduced. */
  url: z.string(),
  styleGuide: StyleGuideSchema,
});

/**
 * The store's funnel from Shopify, with the bands for its industry.
 *
 * The payload is `AuditFunnelSchema` unchanged rather than a briefing-shaped
 * copy of it. The section already carries what the panel has to say — the
 * rates, the bands, which population they describe and when they were
 * measured — and a second shape here would be a second place for "what is this
 * store's add-to-cart rate" to be decided.
 *
 * **Shopify on both sides**, so a merchant can check the store's own rate
 * against their Shopify admin. That is why this region is not fed from the
 * pixel percentiles in `benchmarks`: those are a different instrument, they
 * disagree with Shopify per store, and most stores have no row in them for
 * their first weeks — which is exactly when this is shown.
 */
export const BriefingFunnelSchema = AuditFunnelSchema;

/**
 * The opening read of the store, in prose, across every region above.
 *
 * **A region rather than something the model says in chat.** The summary is
 * dense with figures, and the rule at the top of this file is that a number
 * lives in one place; written here it is the same payload the panel renders,
 * so there is no second copy to drift. The chat message beside it points at
 * this rather than restating it.
 */
export const BriefingSummarySchema = z.object({
  /**
   * Two or three short paragraphs: what this store is, what stands out, and
   * where the opportunity is. Prose rather than fields because the interesting
   * part is which facts it chose to put together, which no fixed shape knows
   * ahead of time.
   */
  text: z.string(),
  /**
   * The version of each analysis it was written from, null for a region that
   * had no answer. Null as a whole when a version is unknown, so it is never
   * reused.
   */
  writtenFrom: z
    .object({
      storeFacts: z.number().nullable(),
      funnel: z.number().nullable(),
    })
    .nullable(),
});

/**
 * One region of the briefing: what we got, or why we did not.
 *
 * Modelled per region rather than as one top-level status because the regions
 * fail independently — a headless store returns perfect order data and no
 * styling at all.
 */
const region = <T extends z.ZodTypeAny>(value: T) =>
  z.object({
    status: BriefingRegionStatusSchema,
    value: value.nullable(),
    /**
     * Merchant-readable, and only set when `status` is not `ok`. This reaches
     * the work log, so it says what we could not learn, never a stack trace.
     */
    reason: z.string().nullish(),
    /**
     * The stored analysis this region was kept as. It names the analysis, not
     * this exact content, which the conversation keeps. Null when the write
     * failed; absent on briefings shown before it.
     */
    recordId: z.string().nullish(),
    /** The schema version of this region's kind. Absent means 0. */
    schemaVersion: z.number().int().min(0).optional(),
  });

/**
 * `summary` is derived from the regions beside it rather than read alongside
 * them, so it is the one region that can be `unavailable` because another
 * region failed rather than because the store had nothing to give.
 */
export const AuditBriefingSchema = z.object({
  /**
   * When the briefing was read from the store, as an ISO time. A reopened
   * conversation shows it as it was then. Optional because briefings shown
   * before it have none.
   */
  readAt: z.string().optional(),
  storeFacts: region(BriefingStoreFactsSchema),
  brand: region(BriefingBrandSchema),
  funnel: region(BriefingFunnelSchema),
  summary: region(BriefingSummarySchema),
});

/**
 * One region as soon as it is read, sent as tool progress while the briefing
 * call is still running, so the panel shows it before the slower regions. The
 * briefing the call returns still carries every region and replaces these.
 */
export const AuditBriefingRegionLandedSchema = z.discriminatedUnion("region", [
  z.object({
    type: z.literal("auditBriefingRegionLanded"),
    region: z.literal("storeFacts"),
    schemaVersion: z.number().int().min(0).optional(),
    value: BriefingStoreFactsSchema,
  }),
  z.object({
    type: z.literal("auditBriefingRegionLanded"),
    region: z.literal("brand"),
    schemaVersion: z.number().int().min(0).optional(),
    value: BriefingBrandSchema,
  }),
  z.object({
    type: z.literal("auditBriefingRegionLanded"),
    region: z.literal("funnel"),
    schemaVersion: z.number().int().min(0).optional(),
    value: BriefingFunnelSchema,
  }),
  z.object({
    type: z.literal("auditBriefingRegionLanded"),
    region: z.literal("summary"),
    schemaVersion: z.number().int().min(0).optional(),
    value: BriefingSummarySchema,
  }),
]);

export const PresentAuditBriefingInputSchema = z.object({});

export const PresentAuditBriefingResultsSchema = z.object({
  type: z.literal("ui-action"),
  action: z.literal("presentAuditBriefing"),
  briefing: AuditBriefingSchema,
});

export const PresentAuditBriefingOutputSchema =
  PresentAuditBriefingResultsSchema;

export type PresentAuditBriefingResults = z.infer<
  typeof PresentAuditBriefingResultsSchema
>;
export type BriefingRegionStatus = z.infer<typeof BriefingRegionStatusSchema>;
export type BriefingStoreFacts = z.infer<typeof BriefingStoreFactsSchema>;
export type BriefingBrand = z.infer<typeof BriefingBrandSchema>;
export type BriefingFunnel = z.infer<typeof BriefingFunnelSchema>;
export type BriefingSummary = z.infer<typeof BriefingSummarySchema>;
export type AuditBriefing = z.infer<typeof AuditBriefingSchema>;
export type AuditBriefingRegionLanded = z.infer<
  typeof AuditBriefingRegionLandedSchema
>;
export type PresentAuditBriefingInput = z.infer<
  typeof PresentAuditBriefingInputSchema
>;
export type PresentAuditBriefingOutput = z.infer<
  typeof PresentAuditBriefingOutputSchema
>;
